import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { HeatmapCell } from '../../../../shared/interfaces/analytics.interface';
import { WEEKDAY_LABELS } from '../../utils/report-format';

interface Row {
  weekday: number;
  label: string;
  total: number;
  cells: { hour: number; count: number; level: number }[];
}

/** Mapa de calor día × hora (hora local): ayuda a decidir horarios y turnos del equipo. */
@Component({
  selector: 'app-busy-hours',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .lvl-0 { background: var(--color-gray-100); }
    .lvl-1 { background: color-mix(in oklab, var(--color-brand-500) 18%, white); }
    .lvl-2 { background: color-mix(in oklab, var(--color-brand-500) 38%, white); }
    .lvl-3 { background: color-mix(in oklab, var(--color-brand-500) 62%, white); color: white; }
    .lvl-4 { background: var(--color-brand-500); color: white; }
    .lvl-5 { background: var(--color-brand-700); color: white; }
    :host-context(.dark) .lvl-0 { background: var(--color-gray-800); }
    :host-context(.dark) .lvl-1 { background: color-mix(in oklab, var(--color-brand-500) 22%, var(--color-gray-900)); }
    :host-context(.dark) .lvl-2 { background: color-mix(in oklab, var(--color-brand-500) 42%, var(--color-gray-900)); }
    :host-context(.dark) .lvl-3 { background: color-mix(in oklab, var(--color-brand-500) 65%, var(--color-gray-900)); }
    :host-context(.dark) .lvl-5 { background: var(--color-brand-300); color: var(--color-gray-900); }
  `,
  template: `
    @if (rows.length === 0) {
      <p class="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No hay citas en estas fechas.</p>
    } @else {
      @if (peak) {
        <p class="mb-4 text-sm text-gray-600 dark:text-gray-300">
          El horario más lleno es el <span class="font-semibold text-gray-900 dark:text-white">{{ peak }}</span>.
          @if (busiestDay) { El día con más citas es el <span class="font-semibold text-gray-900 dark:text-white">{{ busiestDay }}</span>. }
        </p>
      }
      <div class="overflow-x-auto pb-1">
        <table class="w-full min-w-[34rem] border-separate border-spacing-[3px] text-xs">
          <caption class="sr-only">Citas por día de la semana y hora</caption>
          <thead>
            <tr>
              <th scope="col" class="w-12"><span class="sr-only">Día</span></th>
              @for (h of hours; track h) {
                <th scope="col" class="pb-1 text-center font-normal text-gray-500 tabular-nums dark:text-gray-400">{{ h }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.weekday) {
              <tr>
                <th scope="row" class="pr-1 text-left font-medium text-gray-600 dark:text-gray-300">{{ row.label.slice(0, 3) }}</th>
                @for (c of row.cells; track c.hour) {
                  <td class="h-8 rounded text-center font-medium tabular-nums text-gray-800" [class]="'lvl-' + c.level"
                    [title]="row.label + ' ' + c.hour + ':00 — ' + c.count + (c.count === 1 ? ' cita' : ' citas')">
                    @if (c.count > 0) { {{ c.count }} }
                    <span class="sr-only">{{ row.label }} a las {{ c.hour }}:00: {{ c.count }} citas</span>
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
      <div class="mt-3 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400" aria-hidden="true">
        <span>Menos</span>
        @for (l of [0, 1, 2, 3, 4, 5]; track l) {
          <span class="h-3 w-5 rounded-sm" [class]="'lvl-' + l"></span>
        }
        <span>Más citas</span>
      </div>
    }
  `,
})
export class BusyHours {
  hours: number[] = [];
  rows: Row[] = [];
  peak = '';
  busiestDay = '';

  @Input() set data(cells: HeatmapCell[]) {
    const withData = cells.filter((c) => c.count > 0);
    if (withData.length === 0) {
      this.rows = [];
      return;
    }
    const minHour = Math.min(...withData.map((c) => c.hour));
    const maxHour = Math.max(...withData.map((c) => c.hour));
    this.hours = Array.from({ length: maxHour - minHour + 1 }, (_, i) => minHour + i);
    const max = Math.max(...withData.map((c) => c.count));
    const lookup = new Map(withData.map((c) => [`${c.weekday}-${c.hour}`, c.count]));

    this.rows = WEEKDAY_LABELS.map((label, weekday) => {
      const rowCells = this.hours.map((hour) => {
        const count = lookup.get(`${weekday}-${hour}`) ?? 0;
        return { hour, count, level: count === 0 ? 0 : Math.max(1, Math.ceil((count / max) * 5)) };
      });
      return { weekday, label, total: rowCells.reduce((s, c) => s + c.count, 0), cells: rowCells };
    });

    const top = withData.reduce((a, b) => (b.count > a.count ? b : a));
    this.peak = `${WEEKDAY_LABELS[top.weekday].toLowerCase()} de ${top.hour}:00 a ${top.hour + 1}:00`;
    const day = this.rows.reduce((a, b) => (b.total > a.total ? b : a));
    this.busiestDay = day.label.toLowerCase();
  }
}
